import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import CoursesPage from './pages/CoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';
import BookReaderPage from './pages/BookReaderPage';
import MyCoursesPage from './pages/MyCoursesPage';
import ArticlesPage from './pages/ArticlesPage';
import ArticleReadPage from './pages/ArticleReadPage';
import NotificationsPage from './pages/NotificationsPage';
import EditProfilePage from './pages/EditProfilePage';
import AdminDashboard from './pages/AdminDashboard';
import AdminReaders from './pages/AdminReaders';
import AdminReports from './pages/AdminReports';
import AdminArticlesPage from './pages/AdminArticlesPage';
import AdminQuotesPage from './pages/AdminQuotesPage';
import AdminNotificationsPage from './pages/AdminNotificationsPage';
import AdminProductionsPage from './pages/AdminProductionsPage';
import AdminLandingPage from './pages/AdminLandingPage';
import AdminEnrollments from './pages/AdminEnrollments';
import AdminTemplatesPage from './pages/AdminTemplatesPage';
import AdminHistoricalRecordsPage from './pages/AdminHistoricalRecordsPage';
import AdminCourseCelebrationPage from './pages/AdminCourseCelebrationPage';
import AdminDropdownPage from './pages/AdminDropdownPage';
import AdminPermissionsPage from './pages/AdminPermissionsPage';
import AdminAccountsPage from './pages/AdminAccountsPage';
import AdminAnnouncementsPage from './pages/AdminAnnouncementsPage';
import AdminQuestionsPage from './pages/AdminQuestionsPage';
import AdminStreakPage from './pages/AdminStreakPage';
import AdminCommentsPage from './pages/AdminCommentsPage';
import BookQuestionsPage from './pages/BookQuestionsPage';
import MyProductionsPage from './pages/MyProductionsPage';
import HistoricalRecordPage from './pages/HistoricalRecordPage';
import DraftsPage from './pages/DraftsPage';
import LandingPage from './pages/LandingPage';
import RegistrationPage from './pages/RegistrationPage';
import AdminRegistrationsPage from './pages/AdminRegistrationsPage';
import AdminRegistrationSchemaPage from './pages/AdminRegistrationSchemaPage';
import AdminCoursesPage from './pages/AdminCoursesPage';
import AdminPointsPage from './pages/AdminPointsPage';
import AdminInactivePage from './pages/AdminInactivePage';
import AdminBadgesPage from './pages/AdminBadgesPage';
import AdminBadgeProgressPage from './pages/AdminBadgeProgressPage';
import AdminBadgeDigestPage from './pages/AdminBadgeDigestPage';
import ThemeProvider from './components/ThemeProvider';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return null;
  return token ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

const ProtectedRouteNoLayout = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return null;
  return token ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { token, user, loading } = useAuth();
  if (loading) return null;
  if (!token) return <Navigate to="/login" />;
  if (!user?.is_admin) return <Navigate to="/profile" />;
  return <AdminLayout>{children}</AdminLayout>;
};

const LoginRoute = () => {
  const { token, user, loading } = useAuth();
  if (loading) return null;
  if (token && user?.is_admin) return <Navigate to="/admin" />;
  if (token) return <Navigate to="/profile" />;
  return <LoginPage />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/register" element={<RegistrationPage />} />

      {/* Admin routes */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/registrations" element={<AdminRoute><AdminRegistrationsPage /></AdminRoute>} />
      <Route path="/admin/registration-schema" element={<AdminRoute><AdminRegistrationSchemaPage /></AdminRoute>} />
      <Route path="/admin/readers" element={<AdminRoute><AdminReaders /></AdminRoute>} />
      <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
      <Route path="/admin/articles" element={<AdminRoute><AdminArticlesPage /></AdminRoute>} />
      <Route path="/admin/quotes" element={<AdminRoute><AdminQuotesPage /></AdminRoute>} />
      <Route path="/admin/notifications" element={<AdminRoute><AdminNotificationsPage /></AdminRoute>} />
      <Route path="/admin/productions" element={<AdminRoute><AdminProductionsPage /></AdminRoute>} />
      <Route path="/admin/landing" element={<AdminRoute><AdminLandingPage /></AdminRoute>} />
      <Route path="/admin/enrollments" element={<AdminRoute><AdminEnrollments /></AdminRoute>} />
      <Route path="/admin/templates" element={<AdminRoute><AdminTemplatesPage /></AdminRoute>} />
      <Route path="/admin/historical-records" element={<AdminRoute><AdminHistoricalRecordsPage /></AdminRoute>} />
      <Route path="/admin/course-celebration" element={<AdminRoute><AdminCourseCelebrationPage /></AdminRoute>} />
      <Route path="/admin/dropdown" element={<AdminRoute><AdminDropdownPage /></AdminRoute>} />
      <Route path="/admin/permissions" element={<AdminRoute><AdminPermissionsPage /></AdminRoute>} />
      <Route path="/admin/admin-accounts" element={<AdminRoute><AdminAccountsPage /></AdminRoute>} />
      <Route path="/admin/announcements" element={<AdminRoute><AdminAnnouncementsPage /></AdminRoute>} />
      <Route path="/admin/questions" element={<AdminRoute><AdminQuestionsPage /></AdminRoute>} />
      <Route path="/admin/streaks" element={<AdminRoute><AdminStreakPage /></AdminRoute>} />
      <Route path="/admin/comments" element={<AdminRoute><AdminCommentsPage /></AdminRoute>} />
      <Route path="/admin/courses" element={<AdminRoute><AdminCoursesPage /></AdminRoute>} />
      <Route path="/admin/points" element={<AdminRoute><AdminPointsPage /></AdminRoute>} />
      <Route path="/admin/inactive" element={<AdminRoute><AdminInactivePage /></AdminRoute>} />
      <Route path="/admin/badges" element={<AdminRoute><AdminBadgesPage /></AdminRoute>} />
      <Route path="/admin/badges/progress" element={<AdminRoute><AdminBadgeProgressPage /></AdminRoute>} />
      <Route path="/admin/badges/digest" element={<AdminRoute><AdminBadgeDigestPage /></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

      {/* Reader routes */}
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><EditProfilePage /></ProtectedRoute>} />
      <Route path="/courses" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path="/courses/:courseId" element={<ProtectedRoute><CourseDetailPage /></ProtectedRoute>} />
      <Route path="/courses/:courseId/read/:bookKey" element={<ProtectedRouteNoLayout><BookReaderPage /></ProtectedRouteNoLayout>} />
      <Route path="/my-courses" element={<ProtectedRoute><MyCoursesPage /></ProtectedRoute>} />
      <Route path="/articles" element={<ProtectedRoute><ArticlesPage /></ProtectedRoute>} />
      <Route path="/articles/:articleId" element={<ProtectedRouteNoLayout><ArticleReadPage /></ProtectedRouteNoLayout>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/book-questions" element={<ProtectedRoute><BookQuestionsPage /></ProtectedRoute>} />
      <Route path="/productions" element={<ProtectedRoute><MyProductionsPage /></ProtectedRoute>} />
      <Route path="/drafts" element={<ProtectedRoute><DraftsPage /></ProtectedRoute>} />
      <Route path="/historical-record" element={<ProtectedRoute><HistoricalRecordPage /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Toaster position="top-center" richColors />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
